import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function DocumentTypesPage() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listDocTypes();
      setTypes(res.types || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (typeId, data) => {
    try {
      await adminApi.upsertDocType(typeId, data);
      toast.success(`Document type "${data.displayName}" saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (typeId) => {
    if (!confirm('Deactivate this document type?')) return;
    try {
      await adminApi.deleteDocType(typeId);
      toast.success('Document type deactivated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    { key: 'rowKey', label: 'ID', render: (t) => <span className="font-mono text-xs">{t.rowKey}</span> },
    { key: 'displayName', label: 'Name', render: (t) => <span className="font-medium">{t.displayName}</span> },
    { key: 'description', label: 'Description', render: (t) => <span className="text-muted-foreground">{t.description || '—'}</span> },
    { key: 'isActive', label: 'Status', render: (t) => <Badge variant={t.isActive !== false ? 'success' : 'default'}>{t.isActive !== false ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions', label: '', render: (t) => (
        <div className="flex items-center gap-1 justify-end">
          <button onClick={(e) => { e.stopPropagation(); setEditing(t); }} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
          {t.isActive !== false && (
            <button onClick={(e) => { e.stopPropagation(); handleDelete(t.rowKey); }} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Document Types</h1>
          <p className="text-muted-foreground">Configure trackable document categories (insurance, visas, etc.)</p>
        </div>
        <Button onClick={() => setEditing({ rowKey: '', displayName: '', description: '', isActive: true, isNew: true })}>
          <Plus className="h-4 w-4 mr-2" /> Add Type
        </Button>
      </div>
      <DataTable columns={columns} rows={types} loading={loading} emptyMessage="No document types. Create one to start tracking." keyFn={(t) => t.rowKey} />
      {editing && <DocTypeModal type={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
    </div>
  );
}

function DocTypeModal({ type, onClose, onSave }) {
  const [typeId, setTypeId] = useState(type.rowKey || '');
  const [displayName, setDisplayName] = useState(type.displayName || '');
  const [description, setDescription] = useState(type.description || '');
  const [isActive, setIsActive] = useState(type.isActive !== false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!typeId.trim() || !displayName.trim()) return;
    setSaving(true);
    await onSave(typeId.trim().toLowerCase().replace(/\s+/g, '-'), { displayName, description, isActive });
    setSaving(false);
  };

  return (
    <Modal isOpen onClose={onClose} title={type.isNew ? 'Add Document Type' : 'Edit Document Type'}>
      <div className="space-y-4">
        <Input label="Type ID (slug)" value={typeId} onChange={(e) => setTypeId(e.target.value)} placeholder="e.g. insurance, work-permit" disabled={!type.isNew} />
        <Input label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Health Insurance, Work Permit" />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span className="text-sm">Active</span>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !typeId.trim() || !displayName.trim()}>{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </div>
    </Modal>
  );
}
